<script setup lang="ts">
const route = useRoute()
const slug = Array.isArray(route.params.broker_slug) ? route.params.broker_slug.join('/') : route.params.broker_slug

const { fetchBroker, broker } = useBroker()

const { data: content } = await useAsyncData(`broker-${slug}`, async () => {
  return fetchBroker(`${slug}`)
})

const { data: post } = await useAsyncData(`pages-location-${slug}`, async () => {
  return await queryCollection('content').path('/dynamic/broker').first()
})

if (!post.value) {
  throw createError({ statusCode: 404, statusMessage: 'Post not found', fatal: true })
}

const title = computed(() => `${content.value?.broker_name} | Makelaar Statistieken`)

// Dynamic SEO metadata based on broker data
useSeoMeta({
  'title': () =>
    broker.value ? `${broker.value.broker_name} | Makelaar Statistieken` : 'Makelaar Profiel',

  'description': () =>
    broker.value
      ? `Bekijk de prestaties van ${broker.value.broker_name}. Gemiddelde verkoopprijs: ${broker.value.avg_house_price}, Verkochte woningen: ${broker.value.total_houses_sold}, Gemiddelde verkooptijd: ${broker.value.avg_days_on_market} dagen.`
      : 'Gedetailleerde informatie en statistieken over deze makelaar.',

  'ogTitle': () =>
    broker.value ? `${broker.value.broker_name} | Makelaar Statistieken` : 'Makelaar Profiel',

  'ogDescription': () =>
    broker.value
      ? `Bekijk de prestaties van ${broker.value.broker_name}. Gemiddelde verkoopprijs: ${broker.value.avg_house_price}, Verkochte woningen: ${broker.value.total_houses_sold}, Gemiddelde verkooptijd: ${broker.value.avg_days_on_market} dagen.`
      : 'Gedetailleerde informatie en statistieken over deze makelaar.',

  'keywords': () =>
    broker.value
      ? `${broker.value.broker_name}, makelaar beoordeling, makelaarsstatistieken, ${broker.value.price_segment}, ${broker.value.sales_frequency}`
      : 'makelaar beoordeling, makelaarsstatistieken, huizen verkopen',

  // Additional meta tags for rich results
  'og:type': 'profile',
  'profile:first_name': () => broker.value?.broker_name?.split(' ')[0] || '',
  'profile:last_name': () => broker.value?.broker_name?.split(' ').slice(1).join(' ') || '',
  'business:contact_data:locality': () => broker.value?.top_5_locations?.[0]?.name || '',

  // Schema.org metadata
  'structuredData': () => ({
    '@context': 'https://schema.org',
    '@type': 'RealEstateAgent',
    'name': broker.value?.broker_name,
    'description': `Makelaar met ${broker.value?.total_houses_sold} verkochte woningen. Specialist in het ${broker.value?.price_segment}.`,
    'priceRange': `${broker.value?.broker_cost_min} - ${broker.value?.broker_cost_max}`,
    'areaServed': broker.value?.top_5_locations?.map(loc => loc?.name).join(', '),
    'aggregateRating': {
      '@type': 'AggregateRating',
      'ratingValue': '4.5', // You might want to make this dynamic if you have actual ratings
      'reviewCount': broker.value?.total_houses_sold || 0,
    },
  }),
})

defineOgImageComponent('OgImageLocation', {
  title: title,
  description: `Bekijk de prestaties van ${content.value?.broker_name}. Gemiddelde verkoopprijs, Verkochte woningen, Gemiddelde verkooptijd.`,
})

const breadcrumbs = computed(() => [
  {
    label: 'Home',
    to: '/',
    icon: 'i-heroicons-home',
  },
  {
    label: 'Makelaars',
    to: '/makelaars/',
  },
  {
    label: broker.value?.broker_name,
    to: `/makelaar/${slug}/`,
  },
])
</script>

<template>
  <UContainer v-if="post">
    <UPageHeader
      :title="title"
      :description="post.description"
    >
      <UBreadcrumb
        :items="breadcrumbs"
        class="mt-4"
      />
    </UPageHeader>

    <UPage>
      <UPageBody class="prose">
        
        <ContentRenderer
          :value="post"
          class="prose"
          :data="content || {}"
        />
      </UPageBody>

      <template #right>
        <div class="relative">
          <div class="mt-12 sticky top-24 z-10">
            <SidebarRequestBroker
              :broker-slug="slug"
              :broker-name="broker?.broker_name"
              class="static"
            />
          </div>
        </div>
      </template>
    </UPage>

    <BrokerSimilar
      v-if="broker"
      :broker="broker"
    />
  </UContainer>
</template>
